import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, TextField, InputAdornment, Box, TableSortLabel } from '@mui/material';
import React, { useState, useMemo, useCallback, useContext } from 'react';
import { PlayerShirtImage } from "../player/player-shirt-image";
import SearchIcon from '@mui/icons-material/Search';
import { SiteVarsContext } from "../../contexts/sitevars-context";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";

function getPlayerValue(player, key) {
  if (key === 'cost') {
    return player.originalCost;
  } else if (key === 'totalPoints') {
    return player.totalPoints;
  } else if (key === 'position') {
    return player.positionId;
  }
  return null;
}

function multiLevelComparator(a, b, sortConfig) {
  for (let i = 0; i < sortConfig.length; i++) {
    const { key, direction } = sortConfig[i];
    const aValue = getPlayerValue(a, key);
    const bValue = getPlayerValue(b, key);

    let comparison = 0;
    if (aValue < bValue) {
      comparison = -1;
    } else if (aValue > bValue) {
      comparison = 1;
    }

    if (comparison !== 0) {
      return direction === 'desc' ? -comparison : comparison;
    }
  }
  return 0;
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


export const AuctionListTable = ({ players }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState([
    { key: 'position', direction: 'asc' },
    { key: 'cost', direction: 'desc' },
    { key: 'totalPoints', direction: 'desc' },
  ]);
  const {siteVarsData} = useContext(SiteVarsContext);
  const { getTeamById, getPlayerPositionById } = useSiteVarsFunctions();

  const handleRequestSort = useCallback((property) => {
    setSortConfig((prevSortConfig) => {
      const currentSort = prevSortConfig.find(config => config.key === property);
      let newSortConfig = [];

      if (currentSort) {
        // If already sorting by this property, reverse its direction
        const newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
        newSortConfig = [{ key: property, direction: newDirection }];
        // Add other default sorts, excluding the current property if it's already there
        const defaultSorts = [
          { key: 'position', direction: 'asc' },
          { key: 'cost', direction: 'desc' },
          { key: 'totalPoints', direction: 'desc' },
        ].filter(config => config.key !== property);
        newSortConfig = [...newSortConfig, ...defaultSorts];
      } else {
        // If not sorting by this property, make it the primary sort (asc by default)
        newSortConfig = [{ key: property, direction: 'asc' }];
        const defaultSorts = [
          { key: 'position', direction: 'asc' },
          { key: 'cost', direction: 'desc' },
          { key: 'totalPoints', direction: 'desc' },
        ].filter(config => config.key !== property);
        newSortConfig = [...newSortConfig, ...defaultSorts];
      }
      return newSortConfig;
    });
  }, []);

  const debouncedSetSearchTerm = useCallback((value) => {
    const handler = setTimeout(() => {
      setSearchTerm(value);
    }, 300); // 300ms debounce time

    return () => {
      clearTimeout(handler);
    };
  }, []);

  const filteredAndSortedPlayers = useMemo(() => {
    let filteredPlayers = players.filter(player => {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const team = getTeamById(player.teamId);
      const position = getPlayerPositionById(player.positionId);
      return (
        player.lastName?.toLowerCase()?.includes(lowerCaseSearchTerm) ||
        (team && team.name && team.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (position && position.name_short && position.name_short.toLowerCase().includes(lowerCaseSearchTerm))
      );
    });

    return stableSort(filteredPlayers, (a, b) => multiLevelComparator(a, b, sortConfig));
  }, [players, searchTerm, sortConfig]);
  
  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        label="Search Player, Team, or Position"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => debouncedSetSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, mt: 1.25 }}
      />
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 340 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Id</TableCell>
              <TableCell sortDirection={sortConfig[0].key === 'position' ? sortConfig[0].direction : false}>
              <TableSortLabel
                  active={sortConfig[0].key === 'position'}
                  direction={sortConfig[0].key === 'position' ? sortConfig[0].direction : 'asc'}
                  onClick={() => handleRequestSort('position')}
                >
                Surname
                </TableSortLabel>
                </TableCell>
                <TableCell>
                First Name
              </TableCell>
                <TableCell>
                Team
              </TableCell>
              <TableCell>
                Position
              </TableCell>
              <TableCell sx={{width:"20px"}} sortDirection={sortConfig[0].key === 'cost' ? sortConfig[0].direction : false}>
                <TableSortLabel
                  active={sortConfig[0].key === 'cost'}
                  direction={sortConfig[0].key === 'cost' ? sortConfig[0].direction : 'asc'}
                  onClick={() => handleRequestSort('cost')}
                >
                  Cost
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortConfig[0].key === 'totalPoints' ? sortConfig[0].direction : false}>
                <TableSortLabel
                  active={sortConfig[0].key === 'totalPoints'}
                  direction={sortConfig[0].key === 'totalPoints' ? sortConfig[0].direction : 'asc'}
                  onClick={() => handleRequestSort('totalPoints')}
                >
                  Total Points
                </TableSortLabel>
              </TableCell>
              <TableCell>
                News
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedPlayers.map((player, index) => (
              <TableRow key={`${player.playerId}-${index}`}>
                <TableCell sx={{ paddingLeft: '6px', paddingRight: '6px' }}>{player.playerId}</TableCell>
                <TableCell sx={{ paddingLeft: '6px', paddingRight: '6px' }}>{player.lastName}</TableCell>
                <TableCell sx={{ paddingLeft: '6px', paddingRight: '6px' }}>{player.firstName}</TableCell>
                  <TableCell>{getTeamById(player.teamId).name}</TableCell>
                  <TableCell>{getPlayerPositionById(player.positionId).name}</TableCell>
                <TableCell sx={{width:"20px"}}>£{player.originalCost}m</TableCell>
                <TableCell>{player.totalPoints}</TableCell>
                <TableCell>{player.news}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};